import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { replaceCatalogs } from '../../common/fetch.js';
import { deviceListMsg, deviceListsMsg, omaElisaForCompaniesMsg, t } from '../../common/i18n/index.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { BreadCrumbs } from '../BreadCrumbs/index.js';
import { EditCatalogButtonGroup } from '../CatalogDetails/CatalogPartials.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { CatalogReplaceError } from './CatalogReplaceError';
import { CatalogReplaceOperation, CatalogReplaceProducts } from './CatalogReplaceProducts';
import { getTreeItems } from './CatalogReplaceUtils.js';
import type { FormValues } from './CatalogReplaceProducts';
import type { CompanyVirtualCatalogs } from './CatalogReplaceUtils.js';
import type { Catalog } from '../../generated/api/catalog.js';
import type { VirtualCatalog } from '../../generated/api/virtualCatalog.js';
import type { VirtualCatalogReplaceResponse } from '../../generated/api/virtualCatalogReplaceResponse.js';

export interface CatalogReplaceProps {
  virtualCatalog: VirtualCatalog;
  catalog: Catalog;
  companyName: string;
  companyVirtualCatalogs: CompanyVirtualCatalogs[];
}

export const CatalogReplace = ({
  catalog,
  virtualCatalog,
  companyName,
  companyVirtualCatalogs,
}: CatalogReplaceProps) => {
  const [replaceErrorResponse, setReplaceErrorResponse] = useState<VirtualCatalogReplaceResponse>();
  const navigate = useNavigate();
  const { search } = useLocation();

  const methods = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      selectedCatalogs: [],
      selectedSavingOption: CatalogReplaceOperation.PUBLISH,
    },
  });
  const runtimeValues = methods.watch();

  const catalogTreeItems = getTreeItems(companyVirtualCatalogs, catalog.productType);

  const onReplaceCatalogs = async (values: FormValues) => {
    if (catalog?.catalogCode) {
      try {
        const res = await replaceCatalogs({
          sourceCatalogCode: catalog.catalogCode,
          targetCatalogCodes: values.selectedCatalogs,
          publish: values.selectedSavingOption === CatalogReplaceOperation.PUBLISH,
        });
        if (res.failed.length > 0) {
          addNotification(t.QJG7(`Product selection copy failed.`), 'error');
          setReplaceErrorResponse(res);
        } else {
          navigate(paths.COMPANY_INFO_CATALOGS, { replace: true });
          addNotification(t.FGVX(`Product selection copied successfully.`), 'success');
        }
      } catch {
        navigate(paths.COMPANY_INFO_CATALOGS, { replace: true });
        addNotification(t.QJG7(`Product selection copy failed.`), 'error');
      }
    }
  };

  const breadCrumbs = (
    <BreadCrumbs
      breadCrumbPaths={[
        { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
        { name: t.COBB(deviceListsMsg), path: paths.COMPANY_INFO_CATALOGS },
        { name: catalog.name },
      ]}
    />
  );

  const content = replaceErrorResponse ? (
    <CatalogReplaceError replaceErrorResponse={replaceErrorResponse} companyVirtualCatalogs={companyVirtualCatalogs} />
  ) : (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onReplaceCatalogs)} noValidate>
          <CatalogReplaceProducts
            treeItems={catalogTreeItems}
            catalog={catalog}
            virtualCatalog={virtualCatalog}
            isSaving={methods.formState.isSubmitting}
          />
          <EditCatalogButtonGroup
            onCancel={() => {
              navigate(
                generatePath(paths.COMPANY_INFO_CATALOG, { catalogCode: virtualCatalog.virtualCatalogCode }) + search
              );
            }}
            saveDisabled={runtimeValues.selectedCatalogs.length === 0 || catalog.catalogCode === null}
            isSaving={methods.formState.isSubmitting}
          />
        </form>
      </FormProvider>
    </div>
  );

  return (
    <DetailsWrapper
      detailsTop={breadCrumbs}
      id={`catalog-details-${catalog.catalogCode}`}
      classes={[`of-catalog-details`]}
      heading={catalog.name}
      headingBottom={companyName}
      headingTop={t.Z0HF(deviceListMsg)}
      heroPicto="billing-account"
    >
      {content}
    </DetailsWrapper>
  );
};
